import { TransportationTypeIcon } from '@/components/icons/TransportationTypeIcon';
import type { LegDTO, SearchJourneyOffersDTO } from '@/types/dto/agent';
import { cn } from '@/utils/cn';
import { isoDurationToMinutes } from '@/utils/dateTime';
import { Icons, Typography } from '@packages/webshop-design-system';
import { useMemo, type FC } from 'react';

interface JourneyCardTimelineProps {
  journey: SearchJourneyOffersDTO;
}

type TripSegment = {
  duration: number;
  lineNumber?: string;
  ptMode?: LegDTO['ptMode'];
  segmentWidth: string | null;
};

type TimelineLayout = {
  durationTotal: number;
  tripSegments: Array<TripSegment>;
};

const JourneyCardTimeline: FC<JourneyCardTimelineProps> = ({ journey }) => {
  const { durationTotal, tripSegments } = useMemo<TimelineLayout>(() => {
    const tripSegments: Array<TripSegment> = (journey.trips ?? []).flatMap(
      (trip) =>
        (trip.legs ?? []).map((leg) => {
          const duration = isoDurationToMinutes(leg.duration);
          return {
            duration: duration ?? 0,
            lineNumber: leg.lineNumber,
            ptMode: leg.ptMode,
            segmentWidth: null,
          };
        })
    );
    let durationTotal = 0;
    let longestDuration = 0;
    tripSegments.forEach(({ duration }) => {
      durationTotal += duration;
      if (duration > longestDuration) {
        longestDuration = duration;
      }
    });
    if (durationTotal > 0) {
      tripSegments.forEach((segment) => {
        if (segment.duration !== longestDuration) {
          segment.segmentWidth = `${((segment.duration ?? 0) / durationTotal) * 100}%`;
        }
      });
    }
    return {
      durationTotal,
      tripSegments,
    };
  }, [journey.trips]);

  if (!tripSegments.length || !durationTotal) {
    return null;
  }

  return (
    <div className="flex w-full items-start overflow-hidden pb-1">
      <OriginDestinationIcon />
      <div className="flex min-w-0 flex-1">
        {tripSegments.map((segment, index) => {
          const ptModeCode = segment.ptMode?.code?.toUpperCase();
          return (
            <div
              key={index}
              className={cn(
                'flex min-w-0 flex-col items-center justify-center gap-1 px-[2px]',
                segment.segmentWidth ? 'flex-none' : 'flex-1',
                {
                  'min-w-[18px]': ptModeCode === 'WALK',
                  'min-w-[32px]': ptModeCode !== 'WALK',
                }
              )}
              style={{
                width: segment.segmentWidth ?? undefined,
              }}
            >
              <div
                className={cn(
                  'flex items-center gap-1 rounded-[4px] px-1 py-[2px]',
                  {
                    'border border-[#003FBF] bg-[#E7EFFF] text-dark':
                      ptModeCode === 'BUS',
                    'text-neutral': ptModeCode === 'WALK',
                  }
                )}
              >
                <TransportationTypeIcon
                  className="text-current"
                  ptMode={segment.ptMode}
                  width={12}
                  height={12}
                />
                {segment.lineNumber && (
                  <Typography
                    variant="label"
                    className="font-bold leading-[12px] text-current"
                  >
                    {segment.lineNumber}
                  </Typography>
                )}
              </div>
              <div
                className={cn('mb-[1px] h-[2px] w-full rounded-[1px]', {
                  'bg-[#003FBF]': ptModeCode === 'BUS',
                  'mt-[1px] bg-[repeating-linear-gradient(to_right,currentColor_0_3px,transparent_3px_6px)] text-neutral':
                    ptModeCode === 'WALK',
                })}
              />
            </div>
          );
        })}
      </div>
      <OriginDestinationIcon />
    </div>
  );
};

export default JourneyCardTimeline;

const OriginDestinationIcon = () => (
  <div className="flex min-w-[16px] flex-col items-center justify-between self-stretch text-primary">
    <Icons.location className="text-current" width={16} height={16} />
    <div className="h-[4px] w-[4px] rounded-full bg-current" />
  </div>
);
